// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { TradesActionToolbar } from '@advisor-app/features/trades';
import { Table } from '@advisor-app/features/ui/table';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { type FC } from 'react';
import { type Column } from 'react-table';
import { type TradeOrder } from '@shared/data-access/interfaces/redux/trades';
import styles from './open-trades.module.css';
import { capitalizeString } from '@shared/ui-kit/utils/capitalize-string';
import { AppIcon, KpiBoxes, type KpiBoxesProps } from '@shared/ui-kit/components';
import { useConst } from '@shared/ui-kit/hooks';

interface OpenTradesProps {
  kpiData: KpiBoxesProps[];
  trades: TradeOrder[];
}

export const OpenTrades: FC<OpenTradesProps> = ({ kpiData, trades }) => {
  const columns = useConst<Array<Column<TradeOrder>>>(() => [
    {
      Header: () => <p className={styles['table-heading']}>Open date</p>,
      Cell: ({ value }) => <p className={styles['table-cell']}>{value}</p>,
      accessor: 'openDate',
    },
    {
      Header: () => <p className={styles['table-heading']}>Symbol</p>,
      Cell: ({ value }) => <p className={styles['table-cell']}>{value}</p>,
      accessor: 'symbol',
    },
    {
      Header: () => <p className={styles['table-heading']}>Price</p>,
      Cell: ({ value }) => (
        <p className={styles['table-cell']}>{formatToMoney(value)}</p>
      ),
      accessor: 'price',
    },
    {
      Header: () => <p className={styles['table-heading']}>Volume</p>,
      Cell: ({ value }) => <p className={styles['table-cell']}>{value}</p>,
      accessor: 'volume',
    },
    {
      Header: '',
      accessor: 'side',
      Cell: ({ value }) => (
        <div className={styles['table-cell']}>
          <AppIcon icon={capitalizeString(value) as any} size="xl" />
          <span className={styles['chip']}>open</span>
        </div>
      ),
      id: 'options',
    },
  ]);

  return (
    <div>
      <TradesActionToolbar
        buttons={{
          downloadTrades: true,
        }}
        onDownloadTrades={() => exportTableToExcel(trades, 'open-trades')}
      />

      <div className="!mt-5 grid grid-cols-4 gap-6">
        {kpiData.map((vals, idx) => (
          <KpiBoxes key={idx} {...vals} />
        ))}
      </div>

      <div className="mt-6">
        <div className={styles['table-container']}>
          <Table noMargin columns={columns} data={trades} sortable />
        </div>
      </div>
    </div>
  );
};
